import { formatDate, NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { SummaryCardComponent } from '@next/core-lib/components';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';

import {
  TableColumn,
  TableColumnStickyTypes,
  TableColumnTypes,
  TableConfig,
  TableComponent,
} from '@next/core-lib/table';
import {
  FeedIntakeUnit,
  WaterIntakeUnit,
} from 'src/app/reference-value-sheet/models/reference-value-sheet.model';
import { UnitOfMeasureSettings } from 'src/app/settings/models/setting.model';

import Utils from '../../../shared/utils';
import {
  Barn,
  Cycle,
  ObservationSettingsEnum,
  TableValueRegistration,
  ValueRegistration,
  WDOsettingEnum,
  WeekRegistration,
} from '../../models/cycle.model';
import { CycleService } from '../../services/cycle.service';

export interface WDORegistrationDialogInputData extends AbstractDialogData {
  selectedBarn?: Barn;
  barns?: Barn[];
  weekRegistrations: WeekRegistration[];
  cycle: Cycle;
  unitOfMeasureSettings: UnitOfMeasureSettings;
}

@Component({
  selector: 'app-wdo-registration-overview',
  templateUrl: './wdo-registration-overview.component.html',
  styleUrls: ['./wdo-registration-overview.component.scss'],
  imports: [
    DialogComponent,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    FormsModule,
    NgFor,
    MatOption,
    NgIf,
    SummaryCardComponent,
    TableComponent,
    MatIcon,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class WDORegistrationOverviewComponent extends AbstractDialog<void> implements OnInit {
  data = inject<WDORegistrationDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<WDORegistrationOverviewComponent, AbstractDialogResultData<void>>>(
      MatDialogRef,
    );
  private readonly locale = inject<LocaleService>(LocaleService);
  private cycleService = inject(CycleService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  barns: Barn[];
  selectedBarnId: number;

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    height: '50vh',
    wrapperWidth: '100%',
  };
  weekTableConfig: TableConfig;
  dayTableConfig: TableConfig;
  weekColumns: TableColumn[] = [
    {
      id: 'period',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('wdo-registration-overview.columns.weeknr'),
      sticky: TableColumnStickyTypes.START,
    },
  ];
  dayColumns: TableColumn[] = [
    {
      id: 'period',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('wdo-registration-overview.columns.date'),
      sticky: TableColumnStickyTypes.START,
    },
  ];
  hasWeekRows: boolean;
  hasDayRows: boolean;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();

    this.data.title = `${this.locale.translate('cycle')} ${
      this.data.cycle.cycleNumber
    } - ${this.locale.translate('wdo-registration-overview.title')}`;

    // get unique barns for selector and pre-select first one if any
    if (!!this.data.barns && !!this.data.selectedBarn) {
      this.barns = this.data.barns;
      this.selectedBarnId = this.data.selectedBarn.id;
    } else {
      this.barns = [...this.data.cycle.dayOldChicks]
        .map((doc) => doc.barn)
        .filter(
          (barn, index, self) =>
            index ===
            self.findIndex(
              (t) => t.id === barn.id && t.name === barn.name && t.surfaceArea === barn.surfaceArea,
            ),
        );
      this.selectedBarnId = !!this.barns && this.barns.length > 0 && this.barns[0].id;
    }
  }

  ngOnInit(): void {
    Object.keys(this.data.cycle.observationSettings).forEach((columnName) => {
      // @ts-ignore
      const observationSetting = this.data.cycle.observationSettings[columnName];
      // @ts-ignore
      const columnNameInEnum: keyof typeof ObservationSettingsEnum = columnName;

      switch (observationSetting) {
        case WDOsettingEnum.PerWeek:
          if (columnNameInEnum === 'litterQuality') {
            this.weekColumns.push({
              id: 'litterQualityLoosePercentage',
              type: TableColumnTypes.NUMBER,
              label: this.locale.translate('wdo-labels.litterQualities.Loose'),
            });
            this.weekColumns.push({
              id: 'litterQualityCakedPercentage',
              type: TableColumnTypes.NUMBER,
              label: this.locale.translate('wdo-labels.litterQualities.Caked'),
            });
            this.weekColumns.push({
              id: 'litterQualityWetPercentage',
              type: TableColumnTypes.NUMBER,
              label: this.locale.translate('wdo-labels.litterQualities.Wet'),
            });
          } else if (columnNameInEnum === 'mortality') {
            this.weekColumns.push({
              id: 'mortality',
              type: TableColumnTypes.STRING,
              label: this.locale.translate(`wdo-labels.${columnName}`),
            });
            this.weekColumns.push({
              id: 'selection',
              type: TableColumnTypes.STRING,
              label: this.locale.translate(`wdo-labels.selection`),
            });
            this.weekColumns.push({
              id: 'selectionSmall',
              type: TableColumnTypes.STRING,
              label: this.locale.translate(`wdo-labels.selectionSmall`),
            });
            this.weekColumns.push({
              id: 'selectionLegProblems',
              type: TableColumnTypes.STRING,
              label: this.locale.translate(`wdo-labels.selectionLegProblems`),
            });
          } else {
            this.weekColumns.push({
              id: ObservationSettingsEnum[columnNameInEnum],
              type: TableColumnTypes.STRING,
              label: this.setColumnLabel(columnName),
            });
          }
          break;
        case WDOsettingEnum.PerDay:
          if (columnNameInEnum === 'litterQuality') {
            this.dayColumns.push({
              id: 'litterQualityLoosePercentage',
              type: TableColumnTypes.NUMBER,
              label: this.locale.translate('wdo-labels.litterQualities.Loose'),
            });
            this.dayColumns.push({
              id: 'litterQualityCakedPercentage',
              type: TableColumnTypes.NUMBER,
              label: this.locale.translate('wdo-labels.litterQualities.Caked'),
            });
            this.dayColumns.push({
              id: 'litterQualityWetPercentage',
              type: TableColumnTypes.NUMBER,
              label: this.locale.translate('wdo-labels.litterQualities.Wet'),
            });
          } else if (columnNameInEnum === 'mortality') {
            this.dayColumns.push({
              id: 'mortality',
              type: TableColumnTypes.STRING,
              label: this.locale.translate(`wdo-labels.${columnName}`),
            });
            this.dayColumns.push({
              id: 'selection',
              type: TableColumnTypes.STRING,
              label: this.locale.translate(`wdo-labels.selection`),
            });
            this.dayColumns.push({
              id: 'selectionSmall',
              type: TableColumnTypes.STRING,
              label: this.locale.translate(`wdo-labels.selectionSmall`),
            });
            this.dayColumns.push({
              id: 'selectionLegProblems',
              type: TableColumnTypes.STRING,
              label: this.locale.translate(`wdo-labels.selectionLegProblems`),
            });
          } else {
            this.dayColumns.push({
              id: ObservationSettingsEnum[columnNameInEnum],
              type: TableColumnTypes.STRING,
              label: this.setColumnLabel(columnName),
            });
          }
          break;
        default:
          break;
      }
    });
    this.setSelectedBarn(this.selectedBarnId);
  }

  setSelectedBarn(barnId: number) {
    this.weekTableConfig = {
      ...this.tableConfigBase,
    };
    this.dayTableConfig = {
      ...this.tableConfigBase,
    };
    // Table for Weekly Observations
    if (this.weekColumns.length > 1) {
      this.weekTableConfig.columns = this.weekColumns;
      this.weekTableConfig.width = `${this.weekColumns.length * 150}px`;
      const datasource = this.data.weekRegistrations
        .filter((weekReg) => weekReg.barnId === barnId)
        .sort((w1, w2) => (w1.weekNr > w2.weekNr ? 1 : -1))
        .map((weekReg) =>
          this.createRegistrationRow(
            WDOsettingEnum.PerWeek,
            { ...weekReg.registrationData },
            weekReg.registrationData,
            this.weekColumns,
            weekReg.startDay,
            weekReg.endDay,
            weekReg.weekNr,
          ),
        )
        .filter((week) => week !== undefined);
      this.hasWeekRows = datasource.length > 0;
      this.weekTableConfig.dataSource = new MatTableDataSource(datasource);
    }

    // Table for Daily Observations
    if (this.dayColumns.length > 1) {
      this.dayTableConfig.columns = this.dayColumns;
      this.dayTableConfig.width = `${this.dayColumns.length * 150}px`;
      const datasource = this.calculateDayRegistrations(this.dayColumns, barnId);
      this.hasDayRows = datasource.length > 0;
      this.dayTableConfig.dataSource = new MatTableDataSource(datasource);
    }
    this.changeDetectorRef.detectChanges();
  }

  private calculateDayRegistrations(
    dayColumns: TableColumn[],
    barnId: number,
  ): ValueRegistration[] {
    const dayRegistrations: ValueRegistration[] = [];
    this.data.weekRegistrations
      .filter((weekReg) => weekReg.barnId === barnId)
      .sort((w1, w2) => (w1.weekNr > w2.weekNr ? 1 : -1))
      .forEach((weekReg) => {
        weekReg.dayRegistrations.map((dayReg) => {
          dayRegistrations.push(
            this.createRegistrationRow(
              WDOsettingEnum.PerDay,
              { ...dayReg.registrationData },
              dayReg.registrationData,
              dayColumns,
              dayReg.day,
            ),
          );
        });
      });
    return dayRegistrations;
  }

  private createRegistrationRow(
    type: string,
    registration: TableValueRegistration,
    registrationData: ValueRegistration,
    columns: TableColumn[],
    startday: number,
    endday?: number,
    weeknr?: number,
  ): TableValueRegistration {
    Object.keys(registrationData).forEach((dataColumn) => {
      // add first column for weeknr or date
      switch (type) {
        case WDOsettingEnum.PerWeek:
          registration = {
            period: this.cycleService.getWeekInfo(
              this.data.cycle.cycleDate,
              this.data.cycle.cycleEndDate,
              weeknr,
              startday,
              endday,
            ),
            ...registration,
            feedIntakeInKg: this.roundToTwoDecimalPlaces(registration.feedIntakeInKg),
            waterIntakeInL: this.roundToTwoDecimalPlaces(registration.waterIntakeInL),
          };
          break;
        case WDOsettingEnum.PerDay: {
          const cycleDate: Date = Utils.toUtcDate(new Date(this.data.cycle.cycleDate));
          cycleDate.setDate(cycleDate.getDate() + startday);
          registration = <TableValueRegistration>{
            period: formatDate(cycleDate, 'd MMM yy', this.locale.getLocale()),
            ...registration,
            feedIntakeInKg: this.roundToTwoDecimalPlaces(registration.feedIntakeInKg),
            waterIntakeInL: this.roundToTwoDecimalPlaces(registration.waterIntakeInL),
          };
          break;
        }
      }

      // remove properties that are not found in the columns
      if (
        columns
          .map((week) => week.id.toLowerCase())
          .map((s) => dataColumn.toLowerCase().includes(s))
          .filter((val) => !!val).length === 0
      ) {
        // @ts-ignore
        delete registration[dataColumn];
      }
    });
    return registration;
  }

  private setColumnLabel(columnName: string): string {
    let columnLabelField = columnName;

    if (columnName === 'feedIntake') {
      columnLabelField =
        this.data.unitOfMeasureSettings.feedIntakeUnit === FeedIntakeUnit.GramPerBird
          ? 'feedIntakeGramPerBird'
          : 'feedIntakeKg';
    }

    if (columnName === 'waterIntake') {
      columnLabelField =
        this.data.unitOfMeasureSettings.waterIntakeUnit === WaterIntakeUnit.MililiterPerBird
          ? 'waterIntakeMililiterPerBird'
          : 'waterIntakeL';
    }

    return this.locale.translate(`wdo-labels.${columnLabelField}`);
  }

  private roundToTwoDecimalPlaces(value?: number): number {
    if (value) {
      return Math.round(value * 100) / 100;
    }

    return null;
  }
}
